/**
 * STORAGE TIERS
 * 
 * Defines subscription tiers, storage limits, and pricing for Polish ALL the Things.
 * 
 * Tier Structure:
 * - Free: 50 MB - Try the app, limited photos
 * - Casual: $2.99/mo - 250 MB - Light collectors
 * - Enthusiast: $4.99/mo - 1 GB - Active hobbyists
 * - Pro: $9.99/mo - 5 GB - Serious collectors
 * - Museum: $24.99/mo - 25 GB - Mega collections (5000+ items)
 * 
 * Business Logic:
 * - Storage is calculated from Firebase Storage usage
 * - Existing photos stay on downgrade (just can't add more)
 * - Grace period allows slight overages before hard block
 */

// Storage tier identifiers
export type StorageTierId = 'free' | 'casual' | 'enthusiast' | 'pro' | 'museum';

// Tier configuration
export interface StorageTier {
  id: StorageTierId;
  name: string;
  emoji: string;
  storageLimitMB: number;
  storageLimitBytes: number;
  priceMonthly: number;          // USD
  priceYearly: number;           // USD (with discount)
  stripePriceIdMonthly?: string; // Stripe price ID for monthly
  stripePriceIdYearly?: string;  // Stripe price ID for yearly
  features: string[];
  recommended?: boolean;
}

// Convert MB to bytes
const MB = 1024 * 1024;
const GB = 1024 * MB;

// Define all tiers
export const STORAGE_TIERS: Record<StorageTierId, StorageTier> = {
  free: {
    id: 'free',
    name: 'First Few',
    emoji: '🌱',
    storageLimitMB: 50,
    storageLimitBytes: 50 * MB,
    priceMonthly: 0,
    priceYearly: 0,
    features: [
      'Up to 50 MB photo storage',
      'Unlimited collection items',
      'Basic organization features',
      'Public profile & sharing',
    ],
  },
  casual: {
    id: 'casual',
    name: 'Casual Collector',
    emoji: '💅',
    storageLimitMB: 250,
    storageLimitBytes: 250 * MB,
    priceMonthly: 2.99,
    priceYearly: 29.99, // ~$2.50/mo, save ~$6
    features: [
      'Up to 250 MB photo storage',
      'Everything in First Few',
      'Priority support',
      'Early access to new features',
    ],
  },
  enthusiast: {
    id: 'enthusiast',
    name: 'Engaged Enthusiast',
    emoji: '✨',
    storageLimitMB: 1024, // 1 GB
    storageLimitBytes: 1 * GB,
    priceMonthly: 4.99,
    priceYearly: 49.99, // ~$4.17/mo, save ~$10
    recommended: true,
    features: [
      'Up to 1 GB photo storage',
      'Everything in Casual Collector',
      'Bulk photo import',
      'Advanced analytics',
    ],
  },
  pro: {
    id: 'pro',
    name: 'Lacquer Lover',
    emoji: '💎',
    storageLimitMB: 5120, // 5 GB
    storageLimitBytes: 5 * GB,
    priceMonthly: 9.99,
    priceYearly: 99.99, // ~$8.33/mo, save ~$20
    features: [
      'Up to 5 GB photo storage',
      'Everything in Engaged Enthusiast',
      'Collection export tools',
      'Custom profile themes',
    ],
  },
  museum: {
    id: 'museum',
    name: 'Polish Pro',
    emoji: '🏛️',
    storageLimitMB: 25600, // 25 GB
    storageLimitBytes: 25 * GB,
    priceMonthly: 24.99,
    priceYearly: 249.99, // ~$20.83/mo, save ~$50
    features: [
      'Up to 25 GB photo storage',
      'Everything in Lacquer Lover',
      'API access (coming soon)',
      'Dedicated support',
      'Beta feature access',
    ],
  },
};

// Get tier by ID
export function getTier(tierId: StorageTierId): StorageTier {
  return STORAGE_TIERS[tierId] || STORAGE_TIERS.free;
}

// Get the default tier for new users
export function getDefaultTier(): StorageTier {
  return STORAGE_TIERS.free;
}

// Get all tiers as array (for displaying pricing table)
export function getAllTiers(): StorageTier[] {
  return Object.values(STORAGE_TIERS);
}

// Get paid tiers only
export function getPaidTiers(): StorageTier[] {
  return Object.values(STORAGE_TIERS).filter(tier => tier.priceMonthly > 0);
}

// Check if a tier is an upgrade from another
export function isUpgrade(currentTierId: StorageTierId, newTierId: StorageTierId): boolean {
  const tierOrder: StorageTierId[] = ['free', 'casual', 'enthusiast', 'pro', 'museum'];
  return tierOrder.indexOf(newTierId) > tierOrder.indexOf(currentTierId);
}

// Get the next tier up (for upgrade prompts)
export function getNextTier(currentTierId: StorageTierId): StorageTier | null {
  const tierOrder: StorageTierId[] = ['free', 'casual', 'enthusiast', 'pro', 'museum'];
  const currentIndex = tierOrder.indexOf(currentTierId);
  if (currentIndex === -1 || currentIndex >= tierOrder.length - 1) {
    return null;
  }
  return STORAGE_TIERS[tierOrder[currentIndex + 1]];
}

// Calculate what percentage of storage is used
export function calculateStoragePercentage(usedBytes: number, tier: StorageTier): number {
  if (tier.storageLimitBytes === 0) return 0;
  return Math.min(100, Math.round((usedBytes / tier.storageLimitBytes) * 100));
}

// Check if user is over their storage limit
export function isOverStorageLimit(usedBytes: number, tier: StorageTier): boolean {
  return usedBytes > tier.storageLimitBytes;
}

// Check if user is approaching their storage limit (>80%)
export function isApproachingLimit(usedBytes: number, tier: StorageTier): boolean {
  return calculateStoragePercentage(usedBytes, tier) >= 80;
}

// Format bytes to human-readable string
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
}

// Format storage limit for display
export function formatStorageLimit(tier: StorageTier): string {
  if (tier.storageLimitMB >= 1024) {
    return `${tier.storageLimitMB / 1024} GB`;
  }
  return `${tier.storageLimitMB} MB`;
}

// Calculate yearly savings
export function calculateYearlySavings(tier: StorageTier): number {
  const monthlyTotal = tier.priceMonthly * 12;
  return Math.round((monthlyTotal - tier.priceYearly) * 100) / 100;
}

// Get recommended tier based on current usage
export function getRecommendedTier(usedBytes: number): StorageTier {
  // Find the smallest tier that fits their usage with some headroom
  const targetBytes = usedBytes * 1.5; // 50% headroom
  
  for (const tier of getAllTiers()) {
    if (tier.storageLimitBytes >= targetBytes) {
      return tier;
    }
  }
  
  // If they need more than Museum, still recommend Museum
  return STORAGE_TIERS.museum;
}
